import React, { useMemo } from 'react';
import assets from '../assets';
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { qv3CheckRequiredQuestions, useSupplierSurvey } from '../hooks';
import { createTokenFetcher } from '../../../views/components/QuestionnaireV3/helpers';
import PageLoading from '../../../components/PageLoading';
import SomethingWentWrong from '../../../components/SomethingWentWrong';
import QuestionnaireV3 from '../../../views/components/QuestionnaireV3';
import SurveyContainer from '../../../components/Survey/Container';
import { Logo } from '../../../components/Survey/icons';
import ImageHeader from '../../../components/Survey/ImageHeader';
import { toast } from 'react-toastify';
import { SupplierQuestionnaireTypes } from 'src/Ghg/interfaces';
import { registrationTokenParam } from '../utils';

interface Props {
  questionnaireType: SupplierQuestionnaireTypes;
}

export default function SupplierSurveyQuestionnaire({
  questionnaireType,
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const survey = useSupplierSurvey();
  const location = useLocation();

  const [searchParams, _] = useSearchParams();
  const registrationToken = searchParams.get('registration_token');

  const token =
    questionnaireType === 'SupplierQuestionnaireEsg'
      ? survey.fetchQv3TokenQuestionnaireEsg
      : survey.fetchQv3TokenQuestionnaireEnvironment;

  const tokenFetcher = useMemo(() => createTokenFetcher(token), [token]);

  if (survey.isError) return <SomethingWentWrong />;
  if (!survey.data) return <PageLoading />;
  if (!survey.data.answer) {
    return <Navigate to={`../initial-questions${location.search}`} replace />;
  }

  const questionnaireAnswerId =
    questionnaireType === 'SupplierQuestionnaireEsg'
      ? survey.data.answer.questionnaire_answer_esg
      : survey.data.answer.questionnaire_answer_environment;

  const nextLink = {
    onClick: async () => {
      const isValid = await qv3CheckRequiredQuestions(
        tokenFetcher,
        questionnaireAnswerId
      );
      if (!isValid) {
        toast.error(t('ghg.supplierSurvey.requiredQuestionsError') as string);
        return;
      }
      survey.submit
        .mutateAsync(questionnaireType)
        .then(() =>
          navigate(`../main-page${registrationTokenParam(registrationToken)}`)
        )
        .catch(() =>
          toast.error(t('ghg.supplierSurvey.submitError') as string, {
            autoClose: 10_000,
            toastId: 'submit-error',
          })
        );
    },
    label: 'ghg.supplierSurvey.send',
  };

  return (
    <SurveyContainer
      logo={<Logo />}
      header={
        <ImageHeader
          imageSrc={assets.questionnaire.image}
          text={t('ghg.supplierSurvey.mainHeader')}
          small
        />
      }
      nextLink={nextLink}
    >
      <QuestionnaireV3
        questionnaireAnswerId={questionnaireAnswerId}
        tokenAuth={tokenFetcher}
      />
    </SurveyContainer>
  );
}
